import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';

import { COUNTRY_LIST } from 'app/constants';
import { Branch } from 'graphql/models/generated';
import { defaultPhoto } from 'models/branch.model';
import { BaseComponent } from 'shared/base/base.component';
import { ConfirmModalService } from 'shared/services/confirm-modal.service';
import { FormService } from 'shared/services/form.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { SettingsService } from 'shared/services/settings.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-branch-basic-info',
  templateUrl: './basic-info.component.html',
  styleUrls: ['./basic-info.component.scss']
})
export class BranchBasicInfoComponent extends BaseComponent implements OnInit {
  branchLogo = '';

  defaultImageSrc = defaultPhoto;

  @Input() isModal = false;

  branch!: Branch;

  dropdownData = {
    countries: COUNTRY_LIST
  };

  form!: UntypedFormGroup;

  formReady = false;

  canSubmit = false;

  get addressForm(): UntypedFormGroup {
    return this.form.controls.address as UntypedFormGroup;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private formService: FormService,
    private settingsService: SettingsService,
    private confirmModalService: ConfirmModalService,
    private storeService: StoreService,
    private permissionsService: PermissionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    (this.isModal
      ? this.storeService.getCurrentBranch()
      : this.storeService.getCurrentAccountBranch())
      .pipe(
        concatMap((currentBranch: Branch) => {
          if ((!currentBranch.imageUrls || !currentBranch.imageUrls.upload) && currentBranch.id) {
            return this.settingsService.getBranchLogo(currentBranch.id).pipe(
              map((branch: Branch) => {
                const res = { ...currentBranch, imageUrls: branch.imageUrls };
                if (!this.isModal) {
                  this.storeService.setCurrentAccountBranch(res);
                }

                return res;
              })
            );
          }

          return of(currentBranch);
        })
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe((branch: Branch) => {
        this.branch = branch;

        this.setupForm();
      });
  }

  setupForm(): void {
    this.storeService.setFormReady(this.formReady);

    const branch: Branch = this.branch || {};

    if (branch.imageUrls?.download) {
      this.branchLogo = branch.imageUrls.download;
    }

    const disabled = !this.isModal;

    this.form = this.fb.group({
      name: [{ value: branch.name || '', disabled }, [Validators.required]],
      name2: [{ value: branch.name2 || '', disabled }, []],
      photo: [''],
      address: this.formService.createAddressGroup(branch.address || {}, true, disabled)
    });

    if (this.permissionsService.cannotUpdateBranch()) {
      this.form.disable({ emitEvent: false });
    } else {
      this.detectFormChanges();
    }

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);
  }

  private detectFormChanges(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.canSubmit = this.form.valid;
      }, (err: any) => {
        console.info(err);
      });
  }

  onSubmit(): void {
    if (!this.canSubmit || !this.branch.id) return;

    const branchId = this.branch.id;

    this.confirmModalService
      .show('SAVE_BRANCH')
      .onConfirm()
      .pipe(
        concatMap(() => this.settingsService.updateBranchWithLogo(
          branchId,
          this.form.value,
          this.branch?.imageUrls?.upload
        ))
      )
      .subscribe({
        next: (branch: Branch) => {
          this.branch = branch;
          this.canSubmit = false;
          this.storeService.setCurrentAccountBranch(this.branch);
        },
        error: (error: any) => {
          console.error('updateBranch.error', error);
        }
      });
  }
}
